import { useId } from "react";

export function Sparkline({
  points, color = "var(--c-accent)", height = 48, label,
}: {
  points: number[];
  color?: string;
  height?: number;
  label?: string;
}) {
  const id = useId();
  if (points.length < 2) return null;

  const w = 160;
  const pad = 4;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;

  const coords = points.map((v, i) => {
    const x = pad + (i / (points.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / span) * (height - pad * 2);
    return [x, y] as const;
  });

  const line = coords.map(([x, y]) => `${x},${y}`).join(" ");
  const area = `${pad},${height - pad} ${line} ${w - pad},${height - pad}`;

  return (
    <svg width={w} height={height} role="img" aria-label={label ?? "Trend"} style={{ display: "block" }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${id})`} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="3.5" fill={color} />
    </svg>
  );
}

export function RingGauge({
  value, max = 100, size = 96, stroke = 8, color = "var(--c-accent)", label, sublabel,
}: {
  value: number;
  max?: number;
  size?: number;
  stroke?: number;
  color?: string;
  label?: string;
  sublabel?: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(1, Math.max(0, value / max));
  const offset = c * (1 - pct);
  const cx = size / 2;
  const cy = size / 2;

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,.2)" strokeWidth={stroke} />
        <circle
          cx={cx} cy={cy} r={r} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: "stroke-dashoffset var(--ui-dur-slow) var(--ui-ease)" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        textAlign: "center", color: "#fff",
      }}>
        <div className="tabnum" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{label ?? `${Math.round(pct * 100)}%`}</div>
        {sublabel && <div style={{ fontSize: "var(--ui-text-meta)", opacity: .75, marginTop: 2 }}>{sublabel}</div>}
      </div>
    </div>
  );
}

export function DonutChart({
  slices,
  size = 148,
  stroke = 18,
  center,
  sublabel,
}: {
  slices: { id: string; label: string; value: number; color: string }[];
  size?: number;
  stroke?: number;
  center?: string;
  sublabel?: string;
}) {
  const visible = slices.filter((s) => s.value > 0);
  const total = visible.reduce((sum, s) => sum + s.value, 0);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;
  let offset = 0;

  return (
    <div className="nl-donut">
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={sublabel ?? "Mix"}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--c-border)" strokeWidth={stroke} />
          {total > 0 && visible.map((s) => {
            const dash = (s.value / total) * c;
            const el = (
              <circle
                key={s.id}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${cx} ${cy})`}
              />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          textAlign: "center",
        }}>
          <div className="tabnum" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: "var(--c-t1)" }}>
            {center ?? (total ? `${Math.round((visible[0].value / total) * 100)}%` : "—")}
          </div>
          {sublabel && (
            <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 2 }}>{sublabel}</div>
          )}
        </div>
      </div>
      <ul className="nl-donut-legend">
        {slices.map((s) => (
          <li key={s.id}>
            <span className="nl-donut-swatch" style={{ background: s.color }} />
            <span>{s.label}</span>
            <span className="tabnum">{s.value.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
